/**
 * Warns before a reload when `armed` (dirty in-memory form state).
 *
 * F5 / Ctrl|Cmd+R is intercepted so Cancel keeps React state. Browser chrome
 * reload still uses the native beforeunload confirm (same as CandidateNew).
 *
 * Overlay/content use z-[100] so the confirm stacks above an open Dialog
 * (both primitives default to z-50).
 */
import React, { useEffect, useRef, useState } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const REFRESH_CONFIRM_MESSAGE =
  'Are you sure you want to refresh this page? Any unsaved changes will be lost.';

export default function UnsavedRefreshGuard({ armed, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const allowUnloadRef = useRef(false);

  useEffect(() => {
    if (!armed) {
      setOpen(false);
      allowUnloadRef.current = false;
      return undefined;
    }
    const onBeforeUnload = (e) => {
      if (allowUnloadRef.current) return undefined;
      e.preventDefault();
      e.returnValue = REFRESH_CONFIRM_MESSAGE;
      return REFRESH_CONFIRM_MESSAGE;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [armed]);

  useEffect(() => {
    if (!armed) return undefined;
    const onKeyDown = (e) => {
      if (allowUnloadRef.current) return;
      const key = e.key?.toLowerCase?.() || '';
      const isReload = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && key === 'r');
      if (!isReload) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [armed]);

  const confirmRefresh = () => {
    allowUnloadRef.current = true;
    setOpen(false);
    window.location.reload();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogPortal>
        <AlertDialogOverlay className="z-[100]" />
        <AlertDialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
          data-testid={`${testIdPrefix}-refresh-confirm-dialog`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh this page?</AlertDialogTitle>
            <AlertDialogDescription>
              {REFRESH_CONFIRM_MESSAGE}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testIdPrefix}-refresh-confirm-cancel`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRefresh}
              data-testid={`${testIdPrefix}-refresh-confirm-continue`}
            >
              Refresh
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
